<template>
  <div
    class="w-[95%] lg:w-[90%] xl:w-[85%] 2xl:w-[75%] mx-auto space-y-4 md:pt-10 lg:pt-0 pb-10 text-nowrap"
  >
    <div
      class="flex flex-col flex-nowrap bg-gray-50 border-[1.5px] rounded-xl p-4 w-full pt-2 mt-4"
    >
      <p class="text-[#061B44] font-bold text-lg md:text-2xl lg:text-3xl">
        {{ $t("orders") }}
      </p>
      <!-- orders list -->
      <div
        v-if="isLoading"
        class="container w-full h-40 flex flex-col items-center justify-center"
      >
        <img
          class="w-20 h-20 animate-spin fill-[#061B44]"
          src="../assets/icons/loading-loader.svg"
          alt="Loading icon"
        />
      </div>
      <div v-if="!isLoading" class="flex flex-col h-min py-4 mt-2 w-full">
        <div class="overflow-x-scroll">
          <div class="flex flex-row border-b pb-2 text-nowrap w-full">
            <p
              class="text-base relative font-medium text-slate-500 min-w-[120px] lg:w-[13%]"
            >
              {{ $t("order-id") }}
            </p>
            <p
              class="text-base font-medium text-slate-500 min-w-[120px] lg:w-[10%]"
            >
              {{ $t("date") }}
            </p>
            <p
              class="text-base font-medium text-slate-500 min-w-[120px] lg:w-[10%]"
            >
              {{ $t('time') }}
            </p>
            <p
              class="text-base font-medium text-slate-500 min-w-[120px] lg:w-[36%]"
            >
              {{ $t("title") }}
            </p>
            <p
              class="text-base font-medium text-slate-500 min-w-[120px] lg:w-[13%]"
            >
              {{ $t("service") }}
            </p>
            <p
              class="text-base font-medium text-slate-500 min-w-[120px] lg:w-[9%]"
            >
              {{ $t("cost") }}
            </p>
            <p
              class="text-base font-medium text-slate-500 min-w-[120px] lg:w-[9%]"
            >
              {{ $t("status") }}
            </p>
          </div>

          <!-- orders list -->
          <div
            v-if="ordersList.length === 0 && !isLoading"
            class="flex items-center justify-center pt-20 pb-20"
          >
            <p class="text-lg text-slate-800 font-semibold">
              {{
                $t(
                  "no-orders-yet-keep-browsing-or-reach-out-if-you-need-assistance"
                )
              }}
            </p>
          </div>
          <div v-if="ordersList.length > 0" class="w-full">
            <div v-for="(order, index) in ordersList" :key="order.orderId || `order-${order.date}-${order.service}-${index}`">
              <div class="flex flex-row items-end py-2 border-b pb-2 w-full">
                <div class="min-w-[120px] lg:w-[13%] relative group">
                  <p
                    v-if="order.orderId"
                    @click="copyToClipboard(order.orderId)"
                    class="text-sm font-bold text-slate-900 cursor-pointer hover:text-blue-600 transition-colors"
                    :title="t('Copy_the_Order_ID_for') || 'Click to copy Order ID'"
                  >
                    {{ shortenedOrderId(order.orderId) }}
                  </p>
                  <p
                    v-else
                    class="text-sm font-bold text-slate-400"
                  >
                    N/A
                  </p>
                </div>
                <p
                  class="text-sm font-normal text-slate-900 min-w-[120px] lg:w-[10%]"
                >
                  {{ formatDate(order.date) }}
                </p>
                <p
                  class="text-sm font-normal text-slate-900 min-w-[120px] lg:w-[10%]"
                >
                  {{ formatTime(order.date) }}
                </p>
                <p
                  class="text-sm font-normal text-slate-900 min-w-[120px] md:w-[12%] lg:w-[36%] overflow-hidden text-ellipsis whitespace-nowrap"
                >
                  {{ getOrderTitleDisplay(order) }}
                </p>
                <p
                  class="text-sm font-normal text-slate-900 min-w-[120px] lg:w-[13%] overflow-hidden text-ellipsis whitespace-nowrap"
                  :title="order.service === 'oneTimeOrders' ? getOrderTitleDisplay(order) : (order.title || undefined)"
                >
                  {{ getServiceDisplayName(order) }}
                </p>
                <p
                  class="text-sm font-normal text-slate-900 min-w-[120px] lg:w-[9%]"
                >
                  <template v-if="order.service === 'oneTimeOrders'">
                    ${{ (Number(order.cost) || 0).toFixed(2) }} USD
                  </template>
                  <template v-else>
                    {{
                      order.service === "textToSpeechRequests" ||
                      order.service === "translateTextRequests"
                        ? order.cost
                        : convertSecondsToMinutes(Number(order.cost))
                    }}
                    {{
                      order.service === "textToSpeechRequests" ||
                      order.service === "translateTextRequests"
                        ? t("chars")
                        : t("min")
                    }}
                  </template>
                </p>
                <div class="min-w-[120px] lg:w-[9%] flex flex-row items-center justify-start">
                  <div
                    :class="{
                      'bg-orange-300 text-orange-900': isPendingStatus(order.status),
                      'bg-green-300 text-green-900': isCompletedStatus(order.status),
                      'bg-red-300 text-red-900': order.status === 'FAILED' || (order.status && order.status.toUpperCase() === 'FAILED'),
                      'bg-gray-300 text-gray-900': order.status === 'CANCELLED' || (order.status && order.status.toUpperCase() === 'CANCELLED'),
                    }"
                    class="text-sm font-semibold px-2 rounded-sm"
                  >
                    {{ getOrderStatus(order.status) }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- navigation card -->
      <div
        v-if="!isLoading"
        class="flex flex-row items-center justify-between w-full"
      >
        <button
          @click="getOrdersPreviousPage"
          type="button"
          :disabled="currentOrdersPage <= 1 || isPaginationLoading"
          class="bg-white text-slate-900 border-slate-600 border rounded-md px-2 h-8 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
        >
          <p v-if="!isPaginationLoading" class="font-normal text-base">{{ $t("previous") }}</p>
          <p v-else class="font-normal text-base">{{ $t("loading") || "Loading..." }}</p>
        </button>
        <p class="text-sm md:text-base text-black font-normal px-2">
          {{ $t("page") }} {{ currentOrdersPage }} {{ $t("of") }}
          {{ totalOrdersPages }}
        </p>
        <button
          @click="getOrdersNextPage"
          type="button"
          :disabled="currentOrdersPage >= totalOrdersPages || isPaginationLoading"
          class="bg-white text-slate-900 border-slate-600 border rounded-md px-2 h-8 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
        >
          <p v-if="!isPaginationLoading" class="font-normal text-base">{{ $t("next") }}</p>
          <p v-else class="font-normal text-base">{{ $t("loading") || "Loading..." }}</p>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onBeforeMount, ref } from "vue";
import { useAuthStore } from "../store/auth";
import { API_BASE_URL, ORDERSHISTORY } from "../utils/appConst";
import "vue3-toastify/dist/index.css";
import { OrdersModel } from "../utils/ordersModel";
import { useI18n } from "vue-i18n";
import { toast } from "vue3-toastify";
import { useRouter } from "vue-router";
import { useHead } from "@vueuse/head";

const ordersHistoryUrl = `${API_BASE_URL}${ORDERSHISTORY}`;
const authStore = useAuthStore();
const router = useRouter();
const isLoading = ref(true);
const isPaginationLoading = ref(false);
const ordersList = ref<OrdersModel[]>([]);
const { t, locale } = useI18n();
const currentOrdersPage = ref(1);
const totalOrdersPages = ref(0);

onBeforeMount(async () => {
  await fetchOrdersHistory();
});

const shortenedOrderId = (orderId: string | null) => {
  if (!orderId) {
    return "N/A";
  }
  if (orderId.length <= 7) {
    return orderId;
  }
  return `${orderId.slice(0, 5)}***${orderId.slice(-2)}`;
};

const copyToClipboard = async (orderId: string | null) => {
  if (!orderId) {
    toast(t("order-id-not-available") || "Order ID not available", {
      autoClose: 2000,
      type: "error",
      theme: "light",
    });
    return;
  }

  // Check if clipboard API is available
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(orderId);
      toast(t("order-id-copied-to-clipboard"), {
        autoClose: 1500,
        type: "success",
        theme: "light",
      });
    } catch (err) {
      // Fallback for clipboard API failure
      fallbackCopyToClipboard(orderId);
    }
  } else {
    // Fallback for older browsers or non-secure contexts
    fallbackCopyToClipboard(orderId);
  }
};

const fallbackCopyToClipboard = (text: string) => {
  const textArea = document.createElement("textarea");
  textArea.value = text;
  textArea.style.position = "fixed";
  textArea.style.left = "-999999px";
  textArea.style.top = "-999999px";
  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();

  try {
    const successful = document.execCommand("copy");
    if (successful) {
      toast(t("order-id-copied-to-clipboard"), {
        autoClose: 1500,
        type: "success",
        theme: "light",
      });
    } else {
      throw new Error("Copy command failed");
    }
  } catch (err) {
    toast(t("failed-to-copy-text-please-try-again") || "Failed to copy. Please try again.", {
      autoClose: 2000,
      type: "error",
      theme: "light",
    });
  } finally {
    document.body.removeChild(textArea);
  }
};

const fetchOrdersHistory = async () => {
  try {
    isLoading.value = true;
    isPaginationLoading.value = false;
    const response = await fetch(ordersHistoryUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authStore.token}`,
        "ngrok-skip-browser-warning": "69420",
      },
      body: JSON.stringify({ page: currentOrdersPage.value }),
    });
    const result = await response.json();
    console.log("result", result.data);

    if (!response.ok) {
      isLoading.value = false;
      switch (result.error) {
        case "Invalid token":
        case "Authorization header missing":
          toast(t("your-session-has-expired-please-log-in-again-to-continue"), {
            autoClose: 2500,
            type: "error",
            theme: "light",
          });
          await new Promise((resolve) => setTimeout(resolve, 2500));
          authStore.logout();
          router.push("/signing");
          break;
        default:
          toast(t("were-fixing-a-technical-issue-so-please-try"), {
            autoClose: 3000,
            type: "error",
            theme: "light",
          });
      }
      return;
    }
    ordersList.value = [];

    if (result.message == "Success" && result.data) {
      totalOrdersPages.value = result.totalpages || 0;

      for (const [service, orders] of Object.entries(result.data)) {
        if (Array.isArray(orders)) {
          ordersList.value.push(
            ...          orders.map((order: any) => ({
              orderId: order.orderId || order.id || order.fileIdentifier || null,
              date: order.createdDate || "",
              status: order.status || "PENDING",
              service: service,
              cost:
                order.cost ??
                order.videoDuration ??
                order.audioDuration ??
                order.textLength ??
                0,
              title: order.title ?? order.audioTitle ?? order.videoTitle ?? "",
              orderSourceType: order.orderSourceType ?? null,
            }))
          );
          ordersList.value.sort(
            (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
          );
        }
      }
    }
    isLoading.value = false;
  } catch (error) {
    console.log(error);
    isLoading.value = false;
    toast(t("were-fixing-a-technical-issue-so-please-try"), {
      autoClose: 3000,
      type: "error",
      theme: "light",
    });
  }
};

function isPendingStatus(status: string): boolean {
  const s = (status || "").toUpperCase();
  return s === "PENDING" || s === "PROCESSING" || status === "pending" || status === "processing";
}

function isCompletedStatus(status: string): boolean {
  const s = (status || "").toUpperCase();
  return s === "COMPLETED" || s === "SUCCEEDED" || status === "completed" || status === "succeeded";
}

function getOneTimeResultRoute(order: { orderId: string; orderSourceType?: string | null }): { path: string; query: { type: string; onetime_order: string } } {
  const source = (order.orderSourceType || "").toLowerCase();
  if (source === "youtube") {
    return { path: "/youtube-transcription", query: { type: "onetime_result", onetime_order: order.orderId } };
  }
  return { path: "/audio-transcription", query: { type: "onetime_result", onetime_order: order.orderId } };
}

function getOrderStatus(status: string): string {
  const s = (status || "").toUpperCase();
  switch (s) {
    case "PENDING":
    case "PROCESSING":
      return t("pending");
    case "COMPLETED":
    case "SUCCEEDED":
      return t("delivered");
    case "FAILED":
      return t("failed");
    case "CANCELLED":
      return t("canceled");
    default:
      return t("pending");
  }
}

const getOrdersNextPage = async () => {
  try {
    if (currentOrdersPage.value >= totalOrdersPages.value || isPaginationLoading.value) {
      return;
    }
    isPaginationLoading.value = true;
    currentOrdersPage.value++;
    await fetchOrdersHistory();
  } catch (error) {
    console.error("getNextPage: ", error);
    toast(t("were-fixing-a-technical-issue-so-please-try"), {
      autoClose: 3000,
      type: "error",
      theme: "light",
    });
  } finally {
    isPaginationLoading.value = false;
  }
};

const getOrdersPreviousPage = async () => {
  try {
    if (currentOrdersPage.value <= 1 || isPaginationLoading.value) {
      return;
    }
    isPaginationLoading.value = true;
    currentOrdersPage.value--;
    await fetchOrdersHistory();
  } catch (error) {
    console.error("getPrevPage: ", error);
    toast(t("were-fixing-a-technical-issue-so-please-try"), {
      autoClose: 3000,
      type: "error",
      theme: "light",
    });
  } finally {
    isPaginationLoading.value = false;
  }
};

function getOneTimeOrderTitleFallback(order: { orderSourceType?: string | null; orderId?: string }): string {
  const source = (order.orderSourceType || "audio").toLowerCase();
  if (source === "youtube") return t("YouTube-video") || "YouTube video";
  if (source === "audio") return t("audio-link") || "Audio link";
  if (source === "video") return t("video-file") || "Video";
  return order.orderId || t("one-time-transcription") || "One-time order";
}

function getOrderTitleDisplay(order: { service: string; title?: string | null; orderSourceType?: string | null; orderId?: string }): string {
  if (order.service === "oneTimeOrders" && !(order.title && order.title.trim())) {
    return getOneTimeOrderTitleFallback(order);
  }
  return order.title && order.title.trim() ? order.title.trim() : "...";
}

function getServiceDisplayName(order: { service: string; title?: string; orderSourceType?: string | null }): string {
  if (order.service === "oneTimeOrders") {
    const raw = (order.orderSourceType || "audio").toLowerCase();
    const source = ["youtube", "video", "audio"].includes(raw) ? raw : "audio";
    if (source === "audio") return t("audio-transcription");
    if (source === "video") return t("video-transcription");
    if (source === "youtube") return t("youtube-transcription");
    return t("one-time-transcription") || "One-time transcription";
  }
  switch (order.service) {
    case "youtubeVideo":
      return t("Youtube");
    case "videosRequests":
      return t("video-file");
    case "externeVideosRequests":
      return t("video-link");
    case "externeAudioRequests":
      return t("audio-link");
    case "audioRequests":
      return t("audio-file");
    case "translateTextRequests":
      return t("text-translation");
    case "textToSpeechRequests":
      return t("voice-over");
    default:
      return "";
  }
}

function convertSecondsToMinutes(seconds: number): number {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  const additionalMinutes =
    remainingSeconds >= 30 ? 1 : remainingSeconds > 1 ? 0.5 : 0;

  return minutes + additionalMinutes;
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "numeric",
    day: "numeric",
  };
  return date.toLocaleDateString(locale.value === "ar" ? "ar-SA" : "en-US", options);
};

const formatTime = (dateString: string) => {
  const date = new Date(dateString);
  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  };
  return date.toLocaleTimeString(locale.value === "ar" ? "ar-SA" : "en-US", timeOptions);
};

useHead({
  title:
    "Orders - Subtitles Tech"
});
</script>
